import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ColumnLayout } from "@unbox/api";
import { Columns03, DotsGrid, Eye, Lock01, Plus } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { Dialog as AriaDialog, Popover as AriaPopover } from "react-aria-components";
import { ButtonUtility } from "@/components/base/buttons/button-utility";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx } from "@/utils/cx";
import {
  type ColumnCatalog,
  columnCount,
  hideColumn,
  type LayoutChange,
  MAX_COLUMNS,
  MIN_COLUMNS,
  moveColumn,
  showColumn,
  visibleIds,
} from "./column-layout";

const WARNING_DURATION_MS = 3500;

const ROW_CLASSNAME = "flex items-center gap-2 rounded-md px-2 py-2 text-sm text-primary";

const LockIcon = ({ tooltip }: { tooltip: string }) => (
  <Tooltip title={tooltip} placement="left">
    <TooltipTrigger className="flex size-5 shrink-0 items-center justify-center text-fg-quaternary">
      <Lock01 className="size-4" />
    </TooltipTrigger>
  </Tooltip>
);

const SectionLabel = ({ children, className }: { children: string; className?: string }) => (
  <p className={cx("px-2 pt-3 pb-1 text-xs font-semibold tracking-[0.06em] text-tertiary uppercase", className)}>
    {children}
  </p>
);

const SortableColumnRow = ({
  id,
  label,
  unhideable,
  atMin,
  onHide,
}: {
  id: string;
  label: string;
  unhideable: boolean;
  atMin: boolean;
  onHide: () => void;
}) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cx(ROW_CLASSNAME, "hover:bg-primary_hover", isDragging && "relative z-10 bg-primary shadow-lg")}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        aria-label={`Reorder ${label}`}
        className="cursor-grab text-fg-quaternary transition-colors hover:text-fg-quaternary_hover active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <DotsGrid className="size-4" />
      </button>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {unhideable ? (
        <LockIcon tooltip={`${label} can't be hidden`} />
      ) : (
        <button
          type="button"
          aria-label={`Hide ${label}`}
          aria-disabled={atMin}
          onClick={onHide}
          className={cx(
            "flex size-5 shrink-0 cursor-pointer items-center justify-center transition-colors",
            atMin ? "text-fg-disabled" : "text-fg-secondary hover:text-fg-quaternary_hover",
          )}
        >
          <Eye className="size-4" />
        </button>
      )}
    </li>
  );
};

export const ConfigureColumnsPopover = ({
  catalog,
  layout,
  identityLabel,
  labels,
  onChange,
  onReset,
}: {
  catalog: ColumnCatalog;
  layout: ColumnLayout;
  identityLabel: string;
  labels: Map<string, string>;
  onChange: (layout: ColumnLayout) => void;
  onReset: () => void;
}) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [warning, setWarning] = useState<string>();
  const warningTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(warningTimer.current), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const optionsById = new Map(catalog.options.map((option) => [option.id, option]));
  const labelFor = (id: string) => labels.get(id) ?? id;
  const visible = visibleIds(layout);
  const pinned = visible.filter((id) => optionsById.get(id)?.lock === "pinned");
  const movable = visible.filter((id) => optionsById.get(id)?.lock !== "pinned");
  const hidden = new Set(layout.hidden);
  const hiddenGroups = catalog.groups
    .map((group) => ({
      group,
      ids: catalog.options
        .filter((option) => option.group === group && hidden.has(option.id))
        .map((option) => option.id),
    }))
    .filter(({ ids }) => ids.length > 0);
  const count = columnCount(layout);
  const atMax = count >= MAX_COLUMNS;
  const atMin = count <= MIN_COLUMNS;

  const apply = (change: LayoutChange) => {
    if ("layout" in change) {
      onChange(change.layout);
      return;
    }
    setWarning(change.warning);
    clearTimeout(warningTimer.current);
    warningTimer.current = setTimeout(() => setWarning(undefined), WARNING_DURATION_MS);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    onChange(moveColumn(catalog, layout, String(active.id), String(over.id)));
  };

  return (
    <>
      <span ref={triggerRef} className="inline-flex">
        <ButtonUtility
          icon={Columns03}
          color="secondary"
          tooltip="Edit columns in table"
          tooltipPlacement="top"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="bg-primary"
        />
      </span>
      <AriaPopover
        triggerRef={triggerRef}
        isOpen={open}
        onOpenChange={setOpen}
        placement="bottom end"
        offset={8}
        className="flex max-h-[min(600px,calc(100vh-32px))] w-[330px] flex-col rounded-xl border border-secondary bg-primary shadow-lg outline-hidden"
      >
        <AriaDialog aria-label="Configure columns" className="flex min-h-0 flex-1 flex-col outline-hidden">
          <div className="flex items-center justify-between gap-3 px-4 pt-3 pb-2">
            <span className="text-sm font-semibold text-primary">Configure columns</span>
            <span
              aria-live="polite"
              className={cx("text-xs font-medium tabular-nums", atMax ? "text-primary" : "text-tertiary")}
            >
              {count} of {MAX_COLUMNS} columns
            </span>
          </div>

          {warning && (
            <p
              role="status"
              className="mx-3 mb-1 rounded-lg border border-utility-warning-200 bg-warning-primary px-3 py-2 text-xs text-warning-primary"
            >
              {warning}
            </p>
          )}

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-1">
            <SectionLabel>Showing</SectionLabel>
            <ul className="flex flex-col">
              <li className={ROW_CLASSNAME}>
                <LockIcon tooltip={`${identityLabel} is always shown`} />
                <span className="min-w-0 flex-1 truncate">{identityLabel}</span>
              </li>
              {pinned.map((id) => (
                <li key={id} className={ROW_CLASSNAME}>
                  <LockIcon tooltip={`${labelFor(id)} is always shown first`} />
                  <span className="min-w-0 flex-1 truncate">{labelFor(id)}</span>
                </li>
              ))}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={movable} strategy={verticalListSortingStrategy}>
                  {movable.map((id) => (
                    <SortableColumnRow
                      key={id}
                      id={id}
                      label={labelFor(id)}
                      unhideable={optionsById.get(id)?.lock === "unhideable"}
                      atMin={atMin}
                      onHide={() => apply(hideColumn(catalog, layout, id))}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </ul>

            <div aria-hidden className="mt-6 border-t border-secondary" />

            <SectionLabel className="pt-5">Add columns</SectionLabel>
            {hiddenGroups.length === 0 ? (
              <p className="px-2 py-3 text-sm text-tertiary">All columns are showing</p>
            ) : (
              hiddenGroups.map(({ group, ids }, index) => (
                <div key={group}>
                  <p className={cx("px-2 pb-1.5 text-sm font-semibold text-primary", index === 0 ? "pt-2" : "pt-6")}>
                    {group}
                  </p>
                  <ul className="ml-2.5 flex flex-col border-l-[1.5px] border-fg-quaternary pl-2">
                    {ids.map((id) => (
                      <li key={id}>
                        <button
                          type="button"
                          aria-disabled={atMax}
                          onClick={() => apply(showColumn(layout, id))}
                          className={cx(
                            "flex w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-primary_hover",
                            atMax ? "cursor-not-allowed text-disabled" : "cursor-pointer text-primary",
                          )}
                        >
                          {labelFor(id)}
                          <Plus className="size-4 shrink-0 text-fg-quaternary" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-between gap-3 border-t border-secondary px-4 pt-2.5 pb-3">
            <button
              type="button"
              onClick={onReset}
              className="cursor-pointer text-sm font-semibold text-tertiary transition-colors hover:text-secondary"
            >
              Reset to default
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="cursor-pointer text-sm font-semibold text-brand-secondary transition-colors hover:text-brand-secondary_hover"
            >
              Done
            </button>
          </div>
        </AriaDialog>
      </AriaPopover>
    </>
  );
};
